// frontend/src/pages/AuthorsPage.js
// E-E-A-T Optimization: Author/Team page for Google Trust Signals
import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { ArrowLeft, Award, Shield, Globe, BookOpen, Linkedin, Twitter, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { GridBackground } from '@/components/GridBackground';
import { MainNavigation } from '@/components/MainNavigation';
import { Footer } from '@/components/Footer';

const TEAM_MEMBERS = [
    {
        id: 'editorial-team',
        name: 'XtreamGate Editorial Team',
        role: 'Lead Content & Research',
        bio: 'Our editorial team consists of certified network engineers, streaming technology specialists, and cord-cutting experts with 50+ combined years of experience in IPTV infrastructure, CDN optimization, and digital media delivery.',
        expertise: ['IPTV Technology', 'Network Infrastructure', 'Streaming Optimization', 'Security & Privacy'],
        credentials: [
            'Certified Network Administrators',
            '40-Point Provider Stress Testing',
            'Real-Time Community Feedback Integration'
        ]
    },
    {
        id: 'alex-chen',
        name: 'Alex Chen',
        role: 'Senior Streaming Analyst',
        bio: 'Alex has 12+ years of experience in IPTV and CDN infrastructure. Previously worked at major streaming platforms optimizing video delivery networks. Specializes in anti-buffering technologies and server load balancing.',
        expertise: ['CDN Architecture', 'Anti-Freeze Technology', 'Load Balancing', '4K Streaming'],
        credentials: [
            'AWS Solutions Architect Certified',
            'Former Streaming Platform Engineer',
            'Published in Streaming Media Magazine'
        ]
    },
    {
        id: 'sarah-martinez',
        name: 'Sarah Martinez',
        role: 'Device Compatibility Specialist',
        bio: 'Sarah tests IPTV applications across 50+ device types including Firestick, Roku, Apple TV, Samsung, LG, and Android platforms. She writes our device setup guides and troubleshooting content.',
        expertise: ['Firestick Setup', 'Smart TV Apps', 'Cross-Platform Testing', 'App Performance'],
        credentials: [
            'Tested 100+ IPTV Applications',
            '500+ Device Configurations Documented',
            'CompTIA A+ Certified'
        ]
    },
    {
        id: 'james-wilson',
        name: 'James Wilson',
        role: 'Legal & Privacy Researcher',
        bio: 'James researches IPTV legality across 50+ jurisdictions and writes our legal guides. He monitors regulatory changes and ensures our content reflects current laws in the US, UK, Canada, and Australia.',
        expertise: ['IPTV Legality', 'Copyright Law', 'Privacy Regulations', 'VPN Compliance'],
        credentials: [
            'Paralegal Certification',
            'Digital Rights Researcher',
            'GDPR Compliance Specialist'
        ]
    }
];

const TeamMemberCard = ({ member, index }) => (
    <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.1 }}
        className="glass rounded-2xl p-6 md:p-8 border border-white/10"
    >
        <div className="flex items-start gap-4 mb-4">
            <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-primary/30 to-primary/10 flex items-center justify-center text-2xl font-bold text-primary border border-primary/20">
                {member.name.split(' ').map(n => n[0]).join('')}
            </div>
            <div>
                <h3 className="text-xl font-bold text-white">{member.name}</h3>
                <p className="text-primary text-sm font-medium">{member.role}</p>
            </div>
        </div>

        <p className="text-white/70 text-sm leading-relaxed mb-4">
            {member.bio}
        </p>

        {/* Expertise Tags */}
        <div className="flex flex-wrap gap-2 mb-4">
            {member.expertise.map((skill, i) => (
                <span key={i} className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-full border border-primary/20">
                    {skill}
                </span>
            ))}
        </div>

        {/* Credentials */}
        <div className="space-y-1">
            {member.credentials.map((cred, i) => (
                <div key={i} className="flex items-center gap-2 text-xs text-white/60">
                    <Award className="w-3 h-3 text-green-400" />
                    {cred}
                </div>
            ))}
        </div>
    </motion.div>
);

export const AuthorsPage = () => {
    // JSON-LD Schema for Organization and Persons
    const orgSchema = {
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": "XtreamGate",
        "url": "https://xtreamgate.com",
        "logo": "https://xtreamgate.com/logo.png",
        "description": "The internet's premier Knowledge Hub for IPTV enthusiasts and cord-cutters.",
        "foundingDate": "2017",
        "numberOfEmployees": {
            "@type": "QuantitativeValue",
            "value": 10
        }
    };

    const teamSchema = TEAM_MEMBERS.slice(1).map(member => ({
        "@context": "https://schema.org",
        "@type": "Person",
        "name": member.name,
        "jobTitle": member.role,
        "worksFor": {
            "@type": "Organization",
            "name": "XtreamGate"
        },
        "description": member.bio,
        "knowsAbout": member.expertise
    }));

    return (
        <div className="min-h-screen relative">
            <Helmet>
                <title>Our Team | XtreamGate Expert Authors & Analysts</title>
                <meta name="description" content="Meet the XtreamGate team: certified network engineers, streaming specialists, and IPTV experts with 50+ years combined experience. Our authors behind every guide." />
                <link rel="canonical" href="https://xtreamgate.com/about/authors" />

                {/* Organization Schema is now handled globally by SiteSchema */}


                {/* Person Schemas */}
                <script type="application/ld+json">
                    {JSON.stringify(teamSchema)}
                </script>
            </Helmet>

            <GridBackground />
            <MainNavigation />

            <main className="py-20 px-4">
                <div className="max-w-6xl mx-auto">
                    <Button asChild variant="outline" className="mb-8 glass border-primary/30">
                        <Link to="/about">
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            Back to About
                        </Link>
                    </Button>

                    {/* Hero Section */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="glass rounded-2xl p-8 md:p-12 mb-12 relative overflow-hidden"
                    >
                        <div className="absolute top-0 right-0 w-64 h-64 bg-primary/10 rounded-full blur-[80px] -translate-y-1/2 translate-x-1/2" />

                        <div className="relative z-10">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center">
                                    <BookOpen className="w-6 h-6 text-primary" />
                                </div>
                                <span className="px-3 py-1 bg-green-500/20 text-green-400 text-xs font-bold rounded-full border border-green-500/30">
                                    VERIFIED EXPERTS
                                </span>
                            </div>

                            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
                                Our Expert Team
                            </h1>
                            <p className="text-xl text-white/70 max-w-2xl mb-6">
                                Meet the certified engineers, analysts, and researchers behind XtreamGate's trusted guides and reviews.
                            </p>

                            {/* Trust Badges */}
                            <div className="flex flex-wrap gap-4">
                                <div className="flex items-center gap-2 px-4 py-2 bg-white/5 rounded-lg border border-white/10">
                                    <Shield className="w-4 h-4 text-green-400" />
                                    <span className="text-sm text-white/70">50+ Years Combined Experience</span>
                                </div>
                                <div className="flex items-center gap-2 px-4 py-2 bg-white/5 rounded-lg border border-white/10">
                                    <Award className="w-4 h-4 text-primary" />
                                    <span className="text-sm text-white/70">Certified Professionals</span>
                                </div>
                                <div className="flex items-center gap-2 px-4 py-2 bg-white/5 rounded-lg border border-white/10">
                                    <Globe className="w-4 h-4 text-blue-400" />
                                    <span className="text-sm text-white/70">27,000+ Monthly Readers</span>
                                </div>
                            </div>
                        </div>
                    </motion.div>

                    {/* Team Grid */}
                    <div className="grid md:grid-cols-2 gap-6">
                        {TEAM_MEMBERS.map((member, index) => (
                            <TeamMemberCard key={member.id} member={member} index={index} />
                        ))}
                    </div>

                    {/* Editorial Standards */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5 }}
                        className="mt-12 glass rounded-2xl p-8 border border-blue-500/20 bg-blue-900/10"
                    >
                        <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                            <Shield className="w-6 h-6 text-blue-400" />
                            Our Editorial Standards
                        </h2>
                        <div className="grid md:grid-cols-3 gap-6 text-white/70">
                            <div>
                                <h3 className="font-bold text-white mb-2">40-Point Testing</h3>
                                <p className="text-sm">Every provider undergoes stress tests for uptime, buffer rates, and VPN compatibility.</p>
                            </div>
                            <div>
                                <h3 className="font-bold text-white mb-2">Community Verified</h3>
                                <p className="text-sm">Real feedback from 27,000+ monthly readers shapes our recommendations.</p>
                            </div>
                            <div>
                                <h3 className="font-bold text-white mb-2">Regular Updates</h3>
                                <p className="text-sm">All guides are reviewed and updated quarterly to reflect current market conditions.</p>
                            </div>
                        </div>
                    </motion.div>

                    {/* Contact CTA */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.6 }}
                        className="mt-8 text-center"
                    >
                        <p className="text-white/60 mb-4">
                            Have questions for our team? We're here to help.
                        </p>
                        <Button asChild variant="outline" className="glass border-primary/30">
                            <a href="mailto:team@xtreamgate.com">
                                <Mail className="w-4 h-4 mr-2" />
                                Contact the Team
                            </a>
                        </Button>
                    </motion.div>
                </div>
            </main>

            <Footer />
        </div>
    );
};
